import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { FilterValues } from "../../../../data-model/filterValues";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { generateFilterPayloadForGenericAuthStatus } from "../utils/generateFilterPayloadForGenericAuthStatus";
/**
 * Hook that computes the Postgres search payload from active filters, search term,
 * and user context. Defers payload generation until the initial data fetch completes
 * to avoid duplicate requests. Uses a single effect with a ref guard so the payload
 * is built immediately once the initial fetch finishes, and recalculated on any
 * dependency change.
 *
 * @param validFilters - Currently applied filter values for this table
 * @param searchTerm - Current search text entered by the user
 * @param initialDataFetchingExecuted - Whether the initial batch fetch has completed
 * @param tabId - Auth status tab identifier used as a master_auth_status filter
 * @returns Object with filterPayload (current search params) and its setter
 */
export const useFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  tabId: string,
) => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const orgId = getOrgIdForFetchExternalWorklist();
  const { getTableDataFromContext } = useTablesContext();
  const tableAttributes = getTableDataFromContext(
    `${TableNames.GENERIC_AUTH_STATUS_ORDERS}_${tabId}`,
  );
  const sortByField = tableAttributes?.sortByField;
  const sortAscending = tableAttributes?.sortAscending;

  const [filterPayload, setFilterPayload] =
    useState<SearchForMedicalPaOrdersFromPostgresParams | null>(null);

  const hasInitialFetchCompleted = useRef(false);

  useEffect(() => {
    if (initialDataFetchingExecuted && !hasInitialFetchCompleted.current) {
      hasInitialFetchCompleted.current = true;
    }

    if (!hasInitialFetchCompleted.current) return;

    const payload = generateFilterPayloadForGenericAuthStatus(
      validFilters,
      searchTerm,
      orgId,
      user?.id ?? "",
      providerDetails?.FacilityId ?? "",
      providerDetails?.IsAdmin ?? false,
      tabId,
      sortByField,
      sortAscending,
    );
    setFilterPayload(payload);
  }, [
    initialDataFetchingExecuted,
    validFilters,
    searchTerm,
    orgId,
    user?.id,
    providerDetails?.FacilityId,
    providerDetails?.IsAdmin,
    tabId,
    sortByField,
    sortAscending,
  ]);

  return { filterPayload, setFilterPayload };
};
